import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Alert, Spinner, Card, Container, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    userName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
    companyID: '',
    isAdmin: false
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch roles
        const rolesResponse = await api.get('/Auth/roles');
        setAvailableRoles(rolesResponse.data);

        // Fetch active companies
        setLoadingCompanies(true);
        const companiesResponse = await api.get('/Companies/active-companies');
        
        const formattedCompanies = companiesResponse.data.map(company => ({
          value: company.companyID,
          text: company.companyName
        }));
        
        setAvailableCompanies(formattedCompanies);
        
        if (formattedCompanies.length > 0) {
          setFormData(prev => ({
            ...prev,
            companyID: formattedCompanies[0].value,
            role: rolesResponse.data.length > 0 ? rolesResponse.data[0].value : ''
          }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 401) {
          toast.error('Session expired. Please login again.');
          navigate('/login');
        } else {
          toast.error('Failed to load required data');
        }
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchData();
  }, [navigate]);

  const validateForm = () => {
    const newErrors = {};
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    if (!formData.userName.trim()) {
      newErrors.userName = 'Username is required';
    } else if (formData.userName.length < 3) {
      newErrors.userName = 'Username must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordRegex.test(formData.password)) {
      newErrors.password = 'Password must be: 8+ chars, uppercase, lowercase, number, and special character';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.companyID) {
      newErrors.companyID = 'Company is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/Auth/register', {
        UserName: formData.userName,
        Email: formData.email,
        Password: formData.password,
        Role: formData.role,
        CompanyID: formData.companyID,
        IsAdmin: formData.isAdmin
      });

      toast.success(response.data.message || 'User registered successfully');
      navigate('/users');
    } catch (error) {
      console.error('Registration error:', error);
      
      if (error.response) {
        if (error.response.status === 400 && error.response.data.errors) {
          const serverErrors = {};
          error.response.data.errors.forEach(err => {
            const field = err.code.toLowerCase();
            serverErrors[field] = err.description;
          });
          setErrors(serverErrors);
        } else {
          toast.error(error.response.data?.message || 'Registration failed. Please try again.');
        }
      } else if (error.request) {
        toast.error('No response from server. Please check your connection.');
      } else {
        toast.error('Registration error. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow">
            <Card.Header className="bg-default text-black">
              <h3 className="text-center mb-0">Register</h3>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="userName">
                      <Form.Label>Username</Form.Label>
                      <Form.Control
                        type="text"
                        name="userName"
                        value={formData.userName}
                        onChange={handleChange}
                        isInvalid={!!errors.userName}
                        placeholder="Enter username"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.userName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="email">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        isInvalid={!!errors.email}
                        placeholder="Enter email"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="password">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        isInvalid={!!errors.password}
                        placeholder="Enter password"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="confirmPassword">
                      <Form.Label>Confirm Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        isInvalid={!!errors.confirmPassword}
                        placeholder="Confirm password"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.confirmPassword}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Text className="d-block mb-3 text-muted">
                  Password must contain at least 8 characters with uppercase, lowercase, number, and special character
                </Form.Text>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="companyID">
                      <Form.Label>Company</Form.Label>
                      {loadingCompanies ? (
                        <div className="d-flex align-items-center">
                          <Spinner animation="border" size="sm" className="me-2" />
                          <span>Loading companies...</span>
                        </div>
                      ) : availableCompanies.length > 0 ? (
                        <Form.Select
                          name="companyID"
                          value={formData.companyID}
                          onChange={handleChange}
                          isInvalid={!!errors.companyID}
                        >
                          {availableCompanies.map((company) => (
                            <option key={company.value} value={company.value}>
                              {company.text}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <Alert variant="warning">
                          No active companies available. Please add a company first.
                        </Alert>
                      )}
                      <Form.Control.Feedback type="invalid">
                        {errors.companyID}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="role">
                      <Form.Label>User Role</Form.Label>
                      <Form.Select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        isInvalid={!!errors.role}
                      >
                        {availableRoles.map((role) => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.role}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4" controlId="isAdmin">
                  <Form.Check
                    type="checkbox"
                    label="Administrator Access"
                    name="isAdmin"
                    checked={formData.isAdmin}
                    onChange={handleChange}
                  />
                </Form.Group>

              <div ></div>

              <div className="row mb-1">
      <div className="col-md-6">
        <div className="row">
          <div className="col-sm-8">
            <Button className='es-button-wide'
                    variant="secondary"
                    type="submit"
                    disabled={isLoading || loadingCompanies || availableCompanies.length === 0}
                    size="lg"
                  >
                    {isLoading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Registering...
                      </>
                    ) : 'Register User'}
                  </Button>                  
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className="row">
          <div className="col-sm-8">
            <Button className='es-button-wide'
                    variant="outline-secondary"
                    size="lg"
                    onClick={() => navigate('/users')}
                  >
                    Cancel
                  </Button>
          </div>
        </div>
      </div>
    </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Register;